import React, { useState } from "react";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import { toastOptions } from "../../../config/Constants";
import { handleError } from "../../../utils/SharedFunctions";
import { getKnaekWalletCall, createKnaekPayoutCall } from "../../../Api/ApiCalls";

export default function Knaek() {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [searched, setSearched] = useState(false);
  const [confirmedEmail, setConfirmedEmail] = useState(""); // Email when data is retrieved

  // Payout modal state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [payoutEmail, setPayoutEmail] = useState(""); // Fixed email for payout

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchEmail.trim()) {
      toast.error("Please enter a user email", toastOptions);
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const response = await getKnaekWalletCall(searchEmail.trim());
      setWalletData(response);
      setConfirmedEmail(searchEmail.trim()); // Set confirmed email only when data is retrieved
      toast.success("Wallet data retrieved successfully", toastOptions);
    } catch (error) {
      handleError(error);
      toast.error("Failed to fetch wallet data", toastOptions);
      setWalletData(null);
      setConfirmedEmail(""); // Clear confirmed email on error
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleOpenPayoutModal = () => {
    setBankAccountNumber("");
    setAccountHolderName("");
    setPayoutEmail(confirmedEmail); // Fix the email for payout
    setShowPayoutModal(true);
  };

  const handleClosePayoutModal = () => {
    setShowPayoutModal(false);
    setBankAccountNumber("");
    setAccountHolderName("");
  };

  const handleProcessPayout = async (e) => {
    e.preventDefault();

    if (!bankAccountNumber.trim() || !accountHolderName.trim()) {
      toast.error("Please fill in all required fields", toastOptions);
      return;
    }

    setPayoutLoading(true);

    try {
      const payoutData = {
        email: payoutEmail.trim(),
        bankAccountNumber: bankAccountNumber.trim(),
        accountHolderName: accountHolderName.trim()
      };

      await createKnaekPayoutCall(payoutData);
      toast.success("Payout processed successfully", toastOptions);

      // Close modal and refresh wallet data
      handleClosePayoutModal();

      // Refresh wallet data to show updated information
      const response = await getKnaekWalletCall(searchEmail.trim());
      setWalletData(response);

    } catch (error) {
      handleError(error);
      toast.error("Failed to process payout", toastOptions);
    } finally {
      setPayoutLoading(false);
    }
  };

  return (
    <div>
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Knaek Wallet Management</h4>
            </div>
            <div className="card-body">
              {/* Search Form */}
              <form onSubmit={handleSearch} className="mb-4">
                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label htmlFor="searchEmail">User Email</label>
                      <input
                        type="email"
                        id="searchEmail"
                        className="form-control"
                        placeholder="Enter user email to search wallet data"
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>&nbsp;</label>
                      <button
                        type="submit"
                        className="btn btn-primary btn-block"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Searching...
                          </>
                        ) : (
                          "Search Wallet"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Results Section */}
              {searched && !loading && (
                <>
                  {walletData ? (
                    <div>
                      {/* Wallet Info Card */}
                      <div className="card mb-4">
                        <div className="card-header bg-primary text-white">
                          <h5 className="mb-0">Wallet Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <p><strong>Email:</strong> {confirmedEmail}</p>
                              <p><strong>Total Amount:</strong> {formatCurrency(walletData.amount)}</p>
                            </div>
                            <div className="col-md-6 text-right">
                              <button
                                className="btn btn-success"
                                onClick={handleOpenPayoutModal}
                                disabled={walletData.amount === 0}
                              >
                                <i className="fas fa-money-bill-wave me-2"></i>
                                Process Payout
                              </button>
                              {walletData.amount === 0 && (
                                <small className="text-muted d-block mt-1">
                                  No funds available for payout
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Payouts Table */}
                      {walletData.payouts && walletData.payouts.length > 0 ? (
                        <div className="card">
                          <div className="card-header">
                            <h5 className="mb-0">Payouts ({walletData.payouts.length})</h5>
                          </div>
                          <div className="card-body">
                            <div className="table-responsive">
                              <table className="table table-striped">
                                <thead>
                                  <tr>
                                    <th>Payout ID</th>
                                    <th>Wallet ID</th>
                                    <th>Amount</th>
                                    <th>Currency</th>
                                    <th>IBAN</th>
                                    <th>Status</th>
                                    <th>Creation Date</th>
                                    <th>Paid Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {walletData.payouts.map((payout, index) => (
                                    <tr key={payout.payoutId || index}>
                                      <td className="text-truncate" style={{maxWidth: '150px'}} title={payout.payoutId}>
                                        {payout.payoutId}
                                      </td>
                                      <td className="text-truncate" style={{maxWidth: '150px'}} title={payout.walletId}>
                                        {payout.walletId}
                                      </td>
                                      <td className={payout.amount < 0 ? 'text-danger' : 'text-success'}>
                                        {formatCurrency(payout.amount, payout.currency)}
                                      </td>
                                      <td>{payout.currency}</td>
                                      <td>{payout.iban}</td>
                                      <td>
                                        <span className={`badge ${
                                          payout.status === 'confirmed' ? 'badge-success' :
                                          payout.status === 'pending' ? 'badge-warning' :
                                          'badge-secondary'
                                        }`}>
                                          {payout.status}
                                        </span>
                                      </td>
                                      <td>{formatDate(payout.creationDate)}</td>
                                      <td>{payout.paidDate ? formatDate(payout.paidDate) : '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-info">
                          <strong>No payouts found</strong> for this wallet.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      <strong>No wallet data found</strong> for the email: {searchEmail}
                    </div>
                  )}
                </>
              )}

              {!searched && (
                <div className="alert alert-info">
                  <strong>Search Instructions:</strong> Enter a user email above to retrieve their Knaek wallet information and payout history.
                </div>
              )}
            </div>
          </div>

          {/* Payout Modal */}
          <Modal show={showPayoutModal} onHide={handleClosePayoutModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>Process Payout</Modal.Title>
            </Modal.Header>
            <form onSubmit={handleProcessPayout}>
              <Modal.Body>
                <div className="mb-3">
                  <p><strong>User Email:</strong> {payoutEmail}</p>
                  <p><strong>Available Amount:</strong> {walletData ? formatCurrency(walletData.amount) : 'N/A'}</p>
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="bankAccountNumber">Bank Account Number / IBAN *</label>
                  <input
                    type="text"
                    id="bankAccountNumber"
                    className="form-control"
                    placeholder="Enter bank account number or IBAN"
                    value={bankAccountNumber}
                    onChange={(e) => setBankAccountNumber(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="accountHolderName">Account Holder Name *</label>
                  <input
                    type="text"
                    id="accountHolderName"
                    className="form-control"
                    placeholder="Enter account holder name"
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                    required
                  />
                </div>

                <div className="alert alert-warning">
                  <strong>Warning:</strong> Please verify the bank account details before processing the payout. This action cannot be undone.
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClosePayoutModal}
                  disabled={payoutLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={payoutLoading || !bankAccountNumber.trim() || !accountHolderName.trim()}
                >
                  {payoutLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-money-bill-wave me-2"></i>
                      Process Payout
                    </>
                  )}
                </button>
              </Modal.Footer>
            </form>
          </Modal>

    </div>
  );
}